import { useCallback, useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BasicSelect, FormCardLayout } from '@main/components';
import Button from '@mui/material/Button';
import { showMessage } from 'app/store/core/messageSlice';
import { useRequest } from '@main/hooks';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import {
  availableLevels,
  availableVersions,
  createApiRepository,
  createApiTag,
} from 'src/app/api/setup';
import { delayFunction } from 'src/app/utils/timer';
import { arraysContainSameValues, convertObjToPickerMono } from 'src/app/utils/conversors';
import { Divider, Typography } from '@mui/material';
import { showConnectionRepository } from 'app/store/modals';
import HelpIcon from '@mui/icons-material/Help';
import { checkpointsInterfaces, getCompletedCheckpoints } from '../Checkpoints/checkpoints';
import { ProgressStep } from '../Components';
import { StepperContext } from '../Context/SetupContext';

const interfaceSchema = yup.object().shape({
  version: yup.string().required('El campo no puede quedar vacío'),
  level: yup.string().required('El campo no puede quedar vacío'),
});

const defaultValues = {
  version: '',
  level: '',
};

export const Interfaces = ({ onSuccess = () => {} }) => {
  const Dispatch = useDispatch();
  const openAlert = useSimpleAlert();
  const { addLogs, companyId, currentCheckpoint } = useContext(StepperContext);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [versionTagsList, setVersionTagsList] = useState([]);
  const [levelTagsList, setLevelTagsList] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(interfaceSchema),
  });
  const { isValid, errors } = formState;
  const watchVersion = watch('version');
  const watchLevel = watch('level');

  // -------- Obteniendo checkpoints
  const handleUpdateSteps = (incomingStep) => {
    const stepIsThere = completedSteps.includes(incomingStep);
    if (stepIsThere) return;
    setCompletedSteps((current) => [...current, incomingStep]);
  };

  useEffect(() => {
    if (currentCheckpoint === undefined || currentCheckpoint === null) return;
    const isThere = checkpointsInterfaces.includes(currentCheckpoint);
    if (!isThere) return;
    console.log(currentCheckpoint);
    const checkpointsList = getCompletedCheckpoints(currentCheckpoint);
    if (checkpointsList !== undefined) {
      console.log(checkpointsList);
      setCompletedSteps(checkpointsList);
    }
  }, [currentCheckpoint]);

  useEffect(() => {
    console.log('completed steps', completedSteps);
    if (completedSteps === undefined) return;
    console.log('has changed completedSteps');
    const isStepsComplete = arraysContainSameValues(completedSteps, checkpointsInterfaces);
    console.log('Esta completo? ', isStepsComplete);
    setIsCompleted(isStepsComplete);
  }, [completedSteps]);

  useEffect(() => {
    if (isCompleted === undefined) return;
    onSuccess(isCompleted);
  }, [isCompleted]);

  // -------- Definicion de requests
  const {
    makeRequest: tryCreateApi,
    isLoading: isLoadingCreateApi,
    status: statusCreateApi,
    logger: loggerCreateApi,
  } = useRequest(createApiRepository);

  const {
    makeRequest: tryCloneApi,
    isLoading: isLoadingCloneApi,
    status: statusCloneApi,
    logger: loggerCloneApi,
  } = useRequest(createApiTag);

  const { makeRequest: tryGetVersionTags, response: responseVersionTags } =
    useRequest(availableVersions);

  const { makeRequest: tryGetLevelTags, response: responseLevelTags } = useRequest(availableLevels);

  const handleExtractMongoKeys = (mongoObj) => {
    const contentList = convertObjToPickerMono(mongoObj);
    setVersionTagsList(contentList);
  };

  useEffect(() => {
    tryGetVersionTags();
    tryGetLevelTags();
  }, []);

  useEffect(() => {
    if (responseVersionTags === undefined) return;
    console.log('Version Tag: ', responseVersionTags);
    handleExtractMongoKeys(responseVersionTags);
  }, [responseVersionTags]);

  useEffect(() => {
    if (responseLevelTags === undefined || !Array.isArray(responseLevelTags)) return;
    console.log('Level Tag: ', responseLevelTags);
    setLevelTagsList(responseLevelTags);
  }, [responseLevelTags]);

  // -------- Loggers
  useEffect(() => {
    if (loggerCreateApi === undefined) return;
    addLogs(loggerCreateApi);
  }, [loggerCreateApi]);

  useEffect(() => {
    if (loggerCloneApi === undefined) return;
    addLogs(loggerCloneApi);
  }, [loggerCloneApi]);

  const tryStepsWizard = async (currentForm) => {
    setDisableButton(true);
    console.log('Current version:', currentForm?.version);
    if (!currentForm || currentForm.version === undefined || currentForm.level === undefined) {
      Dispatch(
        showMessage({
          message: 'No se encontro una version o nivel para inicializar',
        })
      );
      setDisableButton(false);
      return;
    }
    if (companyId === undefined) {
      Dispatch(
        showMessage({
          message: 'No se encontro el ID de compania',
        })
      );
      setDisableButton(false);
      return;
    }
    try {
      // ---- Crear repo API
      const step9ok = await delayFunction(
        completedSteps.includes(checkpointsInterfaces[0]),
        tryCreateApi,
        companyId,
        { ...currentForm }
      );
      if (!step9ok) return;
      handleUpdateSteps(checkpointsInterfaces[0]);
      console.log('Step okii');

      // ---- Crear Tag de API
      const step10ok = await delayFunction(
        completedSteps.includes(checkpointsInterfaces[1]),
        tryCloneApi,
        companyId,
        { ...currentForm }
      );
      if (!step10ok) return;
      handleUpdateSteps(checkpointsInterfaces[1]);
      console.log('Step okii');
    } catch (error) {
      console.log(error);
    } finally {
      setDisableButton(false);
    }
  };

  const handleBypassStep = (stepName) => {
    if (typeof stepName !== 'string' || stepName === undefined) return;
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea omitir este paso?',
    }).then((userResponseOk) => {
      if (!userResponseOk) return;
      handleUpdateSteps(stepName);
      Dispatch(
        showMessage({
          message: `El paso ${stepName} ha sido omitido`,
        })
      );
    });
  };

  const handleManualRetry = (reqToRetry = () => new Promise(), ...args) => {
    reqToRetry(...args)
      .then((isOk) => {
        console.log(isOk);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleViewModalVersion = useCallback(() => {
    if (watchVersion && responseVersionTags) {
      console.log(watchVersion);
      Dispatch(showConnectionRepository(watchVersion, responseVersionTags));
    }
  }, [watchVersion, responseVersionTags]);

  return (
    <>
      <FormCardLayout.FullColum>
        <div className="mt-24 flex flex-row w-full justify-center items-center">
          <form
            name="mongoModeForm"
            noValidate
            className="flex flex-col gap-12 w-full"
            autoComplete="off"
          >
            <div className="flex items-center justify-between gap-10">
              <Typography fontSize="1.8rem">Version global para clonar repositorios</Typography>
              <div className="flex gap-10 items-center justify-center">
                <Controller
                  name="version"
                  control={control}
                  render={({ field }) => (
                    <BasicSelect
                      value={field.value}
                      data={versionTagsList}
                      title="Version de tag"
                      error={!!errors.version}
                      required
                      staticWidth="24rem"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
                <Button
                  onClick={handleViewModalVersion}
                  disabled={!watchVersion || watchVersion.length === 0}
                  variant="text"
                >
                  <HelpIcon
                    sx={{
                      color: !watchVersion || watchVersion.length === 0 ? 'grey' : 'primary.main',
                    }}
                  />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-10">
              <Typography fontSize="1.8rem">Nivel</Typography>
              <div className="flex gap-10 items-center justify-center">
                <Controller
                  name="level"
                  control={control}
                  render={({ field }) => (
                    <BasicSelect
                      value={field.value}
                      data={levelTagsList}
                      title="Nivel"
                      error={!!errors.level}
                      required
                      staticWidth="24rem"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
                <div className="w-[64px]" />
              </div>
            </div>
            <Button
              aria-label="Comenzar"
              onClick={handleSubmit(tryStepsWizard)}
              variant="contained"
              className="w-3/4 md:w-auto"
              color="primary"
              disabled={!isValid || disableButton || isCompleted}
            >
              Comenzar
            </Button>
          </form>
        </div>
      </FormCardLayout.FullColum>
      <FormCardLayout.FullColum>
        <Divider variant="middle" />
      </FormCardLayout.FullColum>
      <FormCardLayout.FullColum>
        <div className="px-32 flex flex-col w-full justify-start items-start">
          <ProgressStep
            onRetry={() => tryStepsWizard()}
            onBypass={() => handleBypassStep(checkpointsInterfaces[0])}
            status={completedSteps.includes(checkpointsInterfaces[0]) ? 'SUCCESS' : statusCreateApi}
            isLoading={isLoadingCreateApi}
            stepName="Creando repositorio API"
            disableOptions={completedSteps.includes(checkpointsInterfaces[0])}
          />
          <ProgressStep
            onRetry={() => tryStepsWizard()}
            onBypass={() => handleBypassStep(checkpointsInterfaces[1])}
            status={completedSteps.includes(checkpointsInterfaces[1]) ? 'SUCCESS' : statusCloneApi}
            isLoading={isLoadingCloneApi}
            stepName="Clonando Repositorio API"
            disableOptions={completedSteps.includes(checkpointsInterfaces[1])}
          />
        </div>
      </FormCardLayout.FullColum>
    </>
  );
};
